import { Constructor, IDisposable } from '@skylab-shared/types';
import { ListenersRegistry } from '@skylab-shared/utils';
import { v4 as uuid } from 'uuid';
import { Weaver } from '../../../aop';
import { BusinessObjectBase, InternalBusinessObjectConfig } from '../../../bo';
import { Domain, ModelBase, PropertyType } from '../../../model';
import { ObservableCollection } from '../../../observable';
import { TaskQueue } from '../../../task';
import { CollectionChangedJoinPoint } from '../joinpoints/collection-changed';
import { PropertyChangedJoinPoint } from '../joinpoints/property-changed';
import {
    AttachedRule, CanExecuteRuleResult, EffectOnAction,
    Rule,
    RuleDefinition,
    RuleFunc,
    RuleFuncAsync, Source,
    Target,
    ValidObservableCollectionActions
} from './rule';

export class CanExecuteEngine<TBo extends BusinessObjectBase<TBoModel>, TBoModel extends ModelBase> extends ListenersRegistry(class { }) implements IDisposable {
    private readonly queue: TaskQueue<RulesQueueRecord>;

    protected bo!: TBo;
    protected domain!: Domain;
    protected ruleDefinitions: RuleDefinition[];
    protected attachedRules: AttachedRule[];

    public readonly result: ObservableCollection<CanExecuteRuleResult>;

    public constructor(private readonly weaver: Weaver) {
        super();

        this.queue = new TaskQueue<RulesQueueRecord>((record, done) => this.processTask(<RulesQueueRecord>record, done), { interval: 25 });
        this.ruleDefinitions = [];
        this.attachedRules = [];

        this.result = new ObservableCollection<CanExecuteRuleResult>();
    }

    //#region Public members

    public attachBusinessObject(bo: TBo): void {
        this.bo = bo;
        this.domain = (<InternalBusinessObjectConfig<ModelBase>>Reflect.getMetadata('skl:bo', bo.constructor)).domain;
    }

    public addRule<TTriggeredByModel extends ModelBase, TEffectOnType>(
        triggeredBy: PropertyType<TTriggeredByModel>[],
        effectOn: EffectOnAction<TEffectOnType>[],
        func: RuleFuncAsync<TBo, TBoModel, TTriggeredByModel> | RuleFunc<TBo, TBoModel, TTriggeredByModel>
    ): this;
    public addRule<TTriggeredByModel extends ModelBase, TEffectOnType>(
        effectOn: EffectOnAction<TEffectOnType>[],
        func: RuleFuncAsync<TBo, TBoModel, TTriggeredByModel> | RuleFunc<TBo, TBoModel, TTriggeredByModel>
    ): this;
    public addRule<TObservable extends ObservableCollection<TObservableModel>, TAction extends keyof typeof ValidObservableCollectionActions, TObservableModel extends ModelBase>(
        effectOn: EffectOnAction<TObservable>[],
        func: RuleFuncAsync<TBo, TBoModel, TObservableModel> | RuleFunc<TBo, TBoModel, TObservableModel>
    ): this;
    public addRule(...args: any[]): this {
        if (args[0] instanceof Array && typeof args[1] === 'function') {
            this.createAndAddRuleDefinition(
                [],
                args[0],
                { func: (...funcArgs) => Promise.resolve(args[1](...funcArgs)) }
            );
        }
        if (args[0] instanceof Array && args[1] instanceof Array && typeof args[2] === 'function') {
            this.createAndAddRuleDefinition(
                args[0],
                args[1],
                { func: (...funcArgs) => Promise.resolve(args[2](...funcArgs)) }
            );
        }
        if (args[0] instanceof Array && args[0][0] instanceof ObservableCollection && typeof args[1] === 'function') {
            this.createAndAddRuleDefinition(
                args[0],
                args[1],
                { func: (...funcArgs) => Promise.resolve(args[1](...funcArgs)) }
            );
        }
        return this;
    }

    public startProcessing(): void {
        this.attachAllRules();
        this.handleDomainChanges();
        this.handleModelChanges();

        this.queue.startProcessing();
    }

    public queueValidation(): void;
    public queueValidation(target: Target): void;
    public queueValidation(property: PropertyType<ModelBase>): void;
    public queueValidation(targetOrProperty?: Target | PropertyType<ModelBase>): void {
        let rules: AttachedRule[] = [];

        if (targetOrProperty instanceof BusinessObjectBase || targetOrProperty instanceof ObservableCollection) {
            rules = this.attachedRules.filter(r => r.attachedToTarget(targetOrProperty));
        } else if (targetOrProperty instanceof Array) {
            rules = this.attachedRules.filter(r => r.triggeredBy(targetOrProperty));
        } else {
            rules = this.attachedRules;
        }

        this.queueAttachedRulesPerformation(rules);
    }

    public waitAsync(): Promise<void> {
        return this.queue.waitAsync();
    }

    public dispose(): void {
        this.queue.stopProcessing();
        this.deregisterAllListeners();
    }

    //#endregion

    //#region Private methods

    //#region Attaching & dettaching rules

    private attachAllRules(): void {
        this.attachRules(this);

        const models = this.getAllModels();
        models.forEach(model => this.attachRules(model));
    }

    private attachRules(model: ModelBase | this): void {
        const rules = this.getRuleDefinitionsToAttach(model);
        const rulesToAttach = this.prepareRulesToAttach(rules, model);
        rulesToAttach
            .forEach(([rule, triggeredBy, target]) => this.attachRule(rule, triggeredBy, target));
    }

    private attachRule(rule: RuleDefinition, trigger: Source, target: Target): void {
        const attachedRule = this.createAttachedRule(rule, trigger, target);
        this.attachedRules.push(attachedRule);
    }

    private dettachRules(model: ModelBase): void {
        const domainModels = this.getAllModels();
        const supposedlyDetachedModels = this.domain.getModels(model).map(([model]) => model);
        const detachedModels = supposedlyDetachedModels.filter(m => domainModels.indexOf(m) === -1);
        detachedModels
            .reduce((all, curr) => [...all, ...this.attachedRules.filter(attachedRule => attachedRule.attachedToSource(curr))], <AttachedRule[]>[])
            .forEach(attachedRule => {
                const dettachedRules = this.attachedRules.splice(this.attachedRules.indexOf(attachedRule), 1);
                this.removeResults(dettachedRules);
            });
    }

    //#region Preparing

    private getRuleDefinitionsToAttach(trigger: ModelBase | this): RuleDefinition[] {
        if (trigger instanceof ModelBase) {
            return this.ruleDefinitions.filter(def => def.triggeredBy.some(prop => trigger instanceof prop[0]));
        }
        return this.ruleDefinitions.filter(def => def.triggeredBy.length === 0);
    }

    private prepareRulesToAttach(rules: RuleDefinition[], trigger: ModelBase | this): RuleToAttach[] {
        return rules
            .filter(rule => {
                if (trigger === this)
                    return true;
                const [[triggeredByModelType]] = rule.triggeredBy;
                return trigger instanceof triggeredByModelType;
            })
            .reduce<RuleToAttach[]>((rulesToAttach, rule) => {
                if (rule.forCollection) {
                    const collections = this.getAllCollections();
                    return [...rulesToAttach, ...collections.map(col => <RuleToAttach>[rule, trigger, col])];
                }
                return [...rulesToAttach, [rule, trigger, this.bo]];
            }, []);
    }

    private createAttachedRule(rule: RuleDefinition, trigger: Source, target: Target): AttachedRule {
        return new AttachedRule(rule, this.bo, trigger, target);
    }

    //#endregion

    //#endregion

    //#region Rule definition creation

    private createAndAddRuleDefinition(
        triggeredBy: PropertyType<ModelBase>[],
        effectOnActions: EffectOnAction<unknown>[],
        rule: Rule
    ): void {
        const ruleDefinition = this.createRuleDefinition(triggeredBy, effectOnActions, rule);
        this.ruleDefinitions.push(ruleDefinition);
    }

    private createRuleDefinition(
        triggeredBy: PropertyType<ModelBase>[],
        effectOnActions: EffectOnAction<any>[],
        rule: Rule
    ): RuleDefinition {
        const definiton = { id: uuid(), triggeredBy, rule };
        const ruleForCollection = (effectOnActions: EffectOnAction<any>[]): effectOnActions is EffectOnAction<ObservableCollection<unknown>>[] =>
            effectOnActions[0][0] === ObservableCollection;
        if (ruleForCollection(effectOnActions)) {
            return {
                ...definiton,
                effectOn: effectOnActions,
                forCollection: true
            };
        }
        return {
            ...definiton,
            effectOn: effectOnActions.map(([type, actionName]) => type.prototype[actionName]),
            forCollection: false
        };
    }

    //#endregion

    //#region Model & domain changes observing

    private handleDomainChanges(): void {
        this.registerListening(this.weaver.register(PropertyChangedJoinPoint, async ctx => {
            const newValue = ctx.model.get<unknown>(ctx.propertyName);

            if (newValue instanceof ModelBase) {
                this.attachRules(newValue);
            }

            newValue instanceof ObservableCollection && newValue.filter(item => item instanceof ModelBase).forEach(model => {
                this.attachRules(model);
            });

            ctx.oldValue instanceof ModelBase && this.dettachRules(ctx.oldValue);
            ctx.oldValue instanceof ObservableCollection && (newValue as ObservableCollection<ModelBase>).forEach(model => this.dettachRules(model));
        }));

        this.registerListening(this.weaver.register(CollectionChangedJoinPoint, async ctx => {
            ctx.newItems.filter(item => item instanceof ModelBase).forEach((model: ModelBase) => {
                this.attachRules(model);
            });

            ctx.oldItems.filter(item => item instanceof ModelBase).forEach((model: ModelBase) => this.dettachRules(model));
        }));
    }

    private handleModelChanges(): void {
        this.registerListening(this.weaver.register(PropertyChangedJoinPoint, async ctx => {
            await this.queuePropertyRulesValidationAsync([<Constructor<ModelBase>>ctx.model.constructor, <keyof ModelBase>ctx.propertyName]);
        }));
    }

    //#endregion

    //#region Rules queuing, performing

    private processTask({ rules }: RulesQueueRecord, done: () => void): void {
        this.performRulesValidationAsync(rules).then(done, done);
    }

    private async performRulesValidationAsync(attachedRules: AttachedRule[]): Promise<void> {
        this.removeResults(attachedRules);

        const results: CanExecuteRuleResult[] = [];

        for (const rule of attachedRules) {
            const violations = await rule.invoke();
            violations.length > 0 && results.push(...violations);
        }

        this.result.push(...results);
    }

    private queuePropertyRulesValidationAsync(property: PropertyType<ModelBase>): void {
        const rulesToInvoke = this.attachedRules.filter(ar => ar.triggeredBy(property));
        this.queueAttachedRulesPerformation(rulesToInvoke);
    }

    private queueAttachedRulesPerformation(attachedRules: AttachedRule[]): void {
        this.queue.push({ rules: attachedRules });
    }

    private removeResults(rules: AttachedRule[]): void {
        rules
            .reduce((all, r) => [...all, ...this.result.filter(vr => vr.id === r.ruleId)], <CanExecuteRuleResult[]>[])
            .forEach(r => this.result.remove(<CanExecuteRuleResult>r));
    }

    //#endregion

    private getAllModels(): ModelBase[] {
        return this.domain.getModels(this.bo.model).map(([model]) => model);
    }

    private getAllCollections(): ObservableCollection<ModelBase>[] {
        return this.domain.getCollections(this.bo.model).map(([collection]) => collection);
    }

    //#endregion
}

type RuleToAttach = [RuleDefinition, Source, Target];

interface RulesQueueRecord {
    rules: AttachedRule[];
}
